import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, UserCircle2, ShieldCheck } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { useAuth } from "@/lib/auth";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Journal" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-ink-900/80 border-b border-white/10"
            data-testid="main-navbar"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-home-logo">
                    <LogoMark size={44} />
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative text-sm tracking-[0.15em] uppercase font-medium transition-colors ${
                                    isActive
                                        ? "text-gold-500"
                                        : "text-white/80 hover:text-gold-500"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {user && typeof user === "object" ? (
                        <>
                            <Link
                                to={user.role === "admin" ? "/admin" : "/portal"}
                                data-testid="nav-portal-link"
                                className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white hover:border-gold-500/60 hover:text-gold-500 transition-all text-sm"
                            >
                                {user.role === "admin" ? (
                                    <ShieldCheck size={16} />
                                ) : (
                                    <UserCircle2 size={16} />
                                )}
                                {user.role === "admin" ? "Admin" : "Portal"}
                            </Link>
                            <button
                                onClick={handleLogout}
                                data-testid="nav-logout-btn"
                                className="p-2 rounded-full text-white/70 hover:text-gold-500 transition-colors"
                                title="Log out"
                            >
                                <LogOut size={18} />
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-login-link"
                                className="text-sm tracking-[0.15em] uppercase text-white/80 hover:text-gold-500"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/booking"
                                data-testid="nav-book-cta"
                                className="px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-semibold text-sm tracking-wider uppercase hover:bg-gold-400 transition-colors"
                            >
                                Book Consultation
                            </Link>
                        </>
                    )}
                </div>

                <button
                    aria-label="Toggle menu"
                    className="lg:hidden p-2 text-white"
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-ink-900/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-base tracking-[0.15em] uppercase font-medium ${
                                        isActive ? "text-gold-500" : "text-white/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="h-px bg-white/10 my-2" />
                        {user && typeof user === "object" ? (
                            <>
                                <Link
                                    to={user.role === "admin" ? "/admin" : "/portal"}
                                    onClick={() => setOpen(false)}
                                    className="text-gold-500 uppercase tracking-[0.15em] text-sm"
                                >
                                    {user.role === "admin" ? "Admin Dashboard" : "Client Portal"}
                                </Link>
                                <button
                                    onClick={() => {
                                        setOpen(false);
                                        handleLogout();
                                    }}
                                    className="text-left text-white/70 uppercase tracking-[0.15em] text-sm"
                                >
                                    Log out
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    onClick={() => setOpen(false)}
                                    className="text-white/80 uppercase tracking-[0.15em] text-sm"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/booking"
                                    onClick={() => setOpen(false)}
                                    className="px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-semibold text-sm tracking-wider uppercase text-center"
                                >
                                    Book Consultation
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
